import React from "react";
import { useTranslation } from "react-i18next";
import {
  DOOR_HANDLE_STYLES,
  DOOR_STYLES,
  DoorHandleStyle,
  DoorStyle,
} from "../../../../features/floorplan3d/symbols/openings/doors/door3DStyle";

type DoorAppearanceGridProps = {
  value: DoorStyle | "";
  mixedLabel: string;
  onChange: (style: DoorStyle) => void;
};

type DoorHandleGridProps = {
  value: DoorHandleStyle | "";
  mixedLabel: string;
  onChange: (style: DoorHandleStyle) => void;
};

const optionButtonClass = (selected: boolean) =>
  [
    "flex flex-col items-center gap-1 rounded border p-1.5 text-[11px] leading-tight transition-colors",
    selected
      ? "border-blue-600 bg-blue-50 text-blue-900"
      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400",
  ].join(" ");

/** Tiny SVG previews for door leaf styles (no asset downloads). */
const AppearanceThumb: React.FC<{ style: DoorStyle }> = ({ style }) => {
  if (style === "paneled") {
    // Top + short mid + solid lock rail + bottom (matches 3D paneled layout).
    return (
      <svg viewBox="0 0 40 48" className="h-10 w-8" aria-hidden>
        <rect x="4" y="2" width="32" height="44" rx="1" fill="#d2c6b4" stroke="#8a7a66" />
        <rect x="10" y="5" width="20" height="13" rx="0.5" fill="none" stroke="#6a5a46" strokeWidth="1.4" />
        <rect x="12" y="7" width="16" height="9" rx="0.3" fill="#b7a790" />
        <rect x="10" y="20" width="20" height="5" rx="0.5" fill="none" stroke="#6a5a46" strokeWidth="1.4" />
        <rect x="12" y="21.2" width="16" height="2.6" rx="0.3" fill="#b7a790" />
        <rect x="10" y="32" width="20" height="11" rx="0.5" fill="none" stroke="#6a5a46" strokeWidth="1.4" />
        <rect x="12" y="34" width="16" height="7" rx="0.3" fill="#b7a790" />
      </svg>
    );
  }
  if (style === "glazed") {
    return (
      <svg viewBox="0 0 40 48" className="h-10 w-8" aria-hidden>
        <rect x="4" y="2" width="32" height="44" rx="1" fill="#c4b5a0" stroke="#8a7a66" />
        <rect x="9" y="8" width="22" height="30" rx="1" fill="#b8d7ea" stroke="#7aa0b8" opacity="0.9" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 40 48" className="h-10 w-8" aria-hidden>
      <rect x="4" y="2" width="32" height="44" rx="1" fill="#c4b5a0" stroke="#8a7a66" />
    </svg>
  );
};

/** Tiny SVG previews for handle styles. */
const HandleThumb: React.FC<{ style: DoorHandleStyle }> = ({ style }) => {
  if (style === "none") {
    return (
      <svg viewBox="0 0 40 48" className="h-10 w-8" aria-hidden>
        <rect x="10" y="4" width="20" height="40" rx="1" fill="#d6d1c8" stroke="#9a948a" />
      </svg>
    );
  }
  if (style === "knob") {
    return (
      <svg viewBox="0 0 40 48" className="h-10 w-8" aria-hidden>
        <rect x="10" y="4" width="20" height="40" rx="1" fill="#d6d1c8" stroke="#9a948a" />
        <circle cx="28" cy="24" r="5" fill="#b0b4b8" stroke="#7a7e82" />
      </svg>
    );
  }
  if (style === "bar") {
    return (
      <svg viewBox="0 0 40 48" className="h-10 w-8" aria-hidden>
        <rect x="10" y="4" width="20" height="40" rx="1" fill="#d6d1c8" stroke="#9a948a" />
        <rect x="26" y="14" width="3" height="20" rx="1.5" fill="#b0b4b8" stroke="#7a7e82" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 40 48" className="h-10 w-8" aria-hidden>
      <rect x="10" y="4" width="20" height="40" rx="1" fill="#d6d1c8" stroke="#9a948a" />
      <circle cx="26" cy="24" r="3" fill="#b0b4b8" stroke="#7a7e82" />
      <rect x="26" y="22" width="10" height="4" rx="1" fill="#b0b4b8" stroke="#7a7e82" />
    </svg>
  );
};

/**
 * Compact thumbnail grid for 3D door leaf appearance (flush / paneled / glazed).
 */
export const DoorAppearanceOptionGrid: React.FC<DoorAppearanceGridProps> = ({
  value,
  mixedLabel,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <div className="px-3 pb-3">
      {value === "" && (
        <div className="mb-1 text-[11px] text-gray-500">{mixedLabel}</div>
      )}
      <div
        className="grid grid-cols-3 gap-2"
        role="listbox"
        aria-label={t("propertyPanel.doorAppearance")}
      >
        {DOOR_STYLES.map((style) => {
          const selected = value === style;
          return (
            <button
              key={style}
              type="button"
              role="option"
              aria-selected={selected}
              aria-label={t(`propertyPanel.doorStyle.${style}`)}
              className={optionButtonClass(selected)}
              onClick={() => onChange(style)}
            >
              <AppearanceThumb style={style} />
              <span className="truncate">{t(`propertyPanel.doorStyle.${style}`)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Compact thumbnail grid for 3D door handles (lever / knob / bar / none).
 */
export const DoorHandleOptionGrid: React.FC<DoorHandleGridProps> = ({
  value,
  mixedLabel,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <div className="px-3 pb-3">
      {value === "" && (
        <div className="mb-1 text-[11px] text-gray-500">{mixedLabel}</div>
      )}
      <div
        className="grid grid-cols-4 gap-2"
        role="listbox"
        aria-label={t("propertyPanel.doorHandles")}
      >
        {DOOR_HANDLE_STYLES.map((style) => {
          const selected = value === style;
          return (
            <button
              key={style}
              type="button"
              role="option"
              aria-selected={selected}
              aria-label={t(`propertyPanel.doorHandleStyle.${style}`)}
              className={optionButtonClass(selected)}
              onClick={() => onChange(style)}
            >
              <HandleThumb style={style} />
              <span className="truncate">
                {t(`propertyPanel.doorHandleStyle.${style}`)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
